import * as React from 'react';
import {useTheme} from '@mui/material/styles';
import {
  Card,
  CardMedia,
  IconButton,
  MobileStepper,
  Skeleton,
  Typography,
  useMediaQuery,
  CardHeader,
  CardContent,
} from '@mui/material';
import {Box} from '@mui/material';
import {ChevronLeftRounded, ChevronRightRounded} from '@mui/icons-material';
import {OpenAPI} from '../../../services/openapi';
import {AdsService} from '../../../services/openapi/services/AdsService';
import APIWrapperContext from '../../../services/APIWrapperContext';

// Definiere Interfaces für bessere Typisierung
interface IAdvertisement {
  id: number;
  img_url: string;
  link: string;
  description: string;
  location: string;
  status: string;
  visible: boolean;
}

interface ILoadedImages {
  [key: string]: boolean;
}

interface IImages {
  [key: string]: string;
}

const BlueAccent = () => <Box sx={{width: 48, height: 4, bgcolor: '#1976d2', borderRadius: 2}} />;

export default function Ads() {
  const theme = useTheme();
  const {call_backend_api} = React.useContext(APIWrapperContext);
  const matchesMd = useMediaQuery(theme.breakpoints.up('md'));

  // State-Variablen
  const [loadingAds, setLoadingAds] = React.useState(true);
  const [rows, setRows] = React.useState<IAdvertisement[]>([]);
  const [adImages, setAdImages] = React.useState<IImages>({});
  const [activeStep, setActiveStep] = React.useState(0);
  const [touchStartX, setTouchStartX] = React.useState(0);
  const [touchEndX, setTouchEndX] = React.useState(0);
  const [autoPlayTimer, setAutoPlayTimer] = React.useState<NodeJS.Timeout | null>(null);
  const [isAnimating, setIsAnimating] = React.useState(false);
  const [direction, setDirection] = React.useState<'left' | 'right' | null>(null);
  const [nextSlideIndex, setNextSlideIndex] = React.useState<number | null>(null);

  // Lade Werbedaten beim ersten Render
  React.useEffect(() => {
    loadAdvertisements();
  }, []);

  // Lade Bilder, wenn Werbedaten verfügbar sind
  React.useEffect(() => {
    if (rows.length > 0) {
      loadImages();
    }
  }, [rows]);

  // Starte Auto-Slider
  React.useEffect(() => {
    // startAutoSlider();
    return () => {
      if (autoPlayTimer) {
        clearInterval(autoPlayTimer);
      }
    };
  }, [rows, adImages]);

  // Funktion zum Laden der Werbedaten
  const loadAdvertisements = () => {
    call_backend_api(
      AdsService.getAllAdsVisibleAdsAdsListVisibleGet(),
      (data: any) => {
        const formattedData = data
          .map((el: any, k: number) => ({...el, id: k}))
          .filter((a: IAdvertisement) => a.visible)
          .sort((a: IAdvertisement, b: IAdvertisement) => (a.id < b.id ? -1 : 1));

        setRows(formattedData);
        setLoadingAds(false);
      },
      undefined,
      undefined,
      false,
      'Werbung geladen',
      'Fehler beim Laden.'
    );
  };

  // Funktion zum Laden der Bilder
  const loadImages = () => {
    rows.forEach((ad: IAdvertisement) => {
      fetch(OpenAPI.BASE + '/ads/image?image_id=' + ad.img_url)
        .then((response) => response.blob())
        .then((myBlob) => {
          const objectURL = URL.createObjectURL(myBlob);
          setAdImages((prevImages) => ({...prevImages, [ad.img_url]: objectURL}));
        })
        .catch(() => {
          const fallbackURL = '/logo.svg';
          setAdImages((prevImages) => ({...prevImages, [ad.img_url]: fallbackURL}));
        });
    });
  };

  // Funktion zum Starten des automatischen Sliders
  const startAutoSlider = () => {
    if (autoPlayTimer) {
      clearInterval(autoPlayTimer);
    }

    const timer = setInterval(() => {
      moveToNextSlide();
    }, 5000);

    setAutoPlayTimer(timer);
  };

  // Navigation Funktionen
  const moveToNextSlide = () => {
    const filteredAds = rows.filter((el) => el.img_url in adImages);
    if (filteredAds.length === 0 || isAnimating) return;

    const next = (activeStep + 1) >= filteredAds.length ? 0 : activeStep + 1;

    // Setup: show current and next side by side
    setDirection('left');
    setNextSlideIndex(next);
    setIsAnimating(true);

    // After animation completes, clean up
    setTimeout(() => {
      setActiveStep(next);
      setIsAnimating(false);
      setDirection(null);
      setNextSlideIndex(null);
    }, 500);
  };

  const moveToPrevSlide = () => {
    const filteredAds = rows.filter((el) => el.img_url in adImages);
    if (filteredAds.length === 0 || isAnimating) return;

    const prev = (activeStep - 1) < 0 ? filteredAds.length - 1 : activeStep - 1;

    // Setup: show prev and current side by side
    setDirection('right');
    setNextSlideIndex(prev);
    setIsAnimating(true);

    // After animation completes, clean up
    setTimeout(() => {
      setActiveStep(prev);
      setIsAnimating(false);
      setDirection(null);
      setNextSlideIndex(null);
    }, 500);
  };

  // Touch-Event-Handler für Swipe-Funktionalität
  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(e.touches[0].clientX);
    // Auto-Slider pausieren, während der Benutzer swiped
    if (autoPlayTimer) {
      clearInterval(autoPlayTimer);
      setAutoPlayTimer(null);
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    setTouchEndX(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (isAnimating) return;

    // Wenn der Swipe mehr als 50px war, zum nächsten/vorherigen Slide wechseln
    if (touchStartX - touchEndX > 50) {
      moveToNextSlide();
    } else if (touchEndX - touchStartX > 50) {
      moveToPrevSlide();
    }

    // Auto-Slider nach dem Swipe wieder starten
    startAutoSlider();
  };

  // Filtere verfügbare Ads
  const filteredAds = rows.filter((el) => el.img_url in adImages);

  // Helper function to get ads for a specific step
  const getAdsForStep = (step: number) => {
    if (filteredAds.length === 0) return [];

    if (!matchesMd) {
      // Mobile: show 1 ad
      return [filteredAds[step]];
    } else {
      // Desktop: show 3 ads
      return filteredAds.length > 1
        ? [
            filteredAds[step],
            filteredAds[(step + 1) % filteredAds.length],
            filteredAds[(step + 2) % filteredAds.length],
          ]
        : [filteredAds[step]];
    }
  };

  const currentAds = getAdsForStep(activeStep);
  const nextAds = nextSlideIndex !== null ? getAdsForStep(nextSlideIndex) : [];

  // Render-Funktionen
  const renderLoadingSkeleton = () => (
    <Box
      component='div'
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: {xs: 'column', md: 'row'},
        flex: 1,
        justifyContent: 'space-between',
        gap: {md: 2, xs: 2},
      }}>
      {(matchesMd ? [0, 1, 2] : [0]).map((_, idx) => (
        <Card
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            maxHeight: {xs: 'fit-content', md: 'inherit'},
            height: '100%',
            width: '100%',
          }}
          key={'ads_skeleton_' + idx}>
          <Box component='div' sx={{flex: 1, display: 'flex', width: '100%'}}>
            <Skeleton
              variant='rounded'
              sx={{
                flex: 1,
                width: '100%',
                height: 'fit-content',
              }}
            />
          </Box>
          <Box component='div' sx={{minHeight: 72, mt: 1, mb: 1}}>
            <Typography variant='body1'>
              <Skeleton variant='text' width={100} />
            </Typography>
            <Typography variant='body1'>
              <Skeleton variant='text' width={70} />
            </Typography>
            <Typography variant='body1'>
              <Skeleton variant='text' width={90} />
            </Typography>
          </Box>
        </Card>
      ))}
    </Box>
  );

  const renderAdCard = (ad: IAdvertisement) => (
    <IconButton
      key={`ad-${ad.id}`}
      sx={{p: 1, fontSize: 'inherit', flex: 1, display: 'flex', alignItems: 'stretch'}}
      href={ad.link}
      target='_blank'>
      <Card
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          flex: 1,
          height: '100%',
          width: '100%',
          position: 'relative',
        }}>
        <Box component='div' sx={{flex: 1, display: 'flex', width: '100%'}}>
          {!adImages[ad.img_url] && (
            <Skeleton
              variant='rounded'
              sx={{
                flex: 1,
                width: '100%',
                height: !adImages[ad.img_url] ? 200 : 0,
              }}
            />
          )}

          <CardMedia
            component='img'
            sx={{
              flex: 1,
              objectFit: 'cover',
              height: adImages[ad.img_url] ? '100%' : 0,
              maxHeight: {xs: 'fit-content', md: 'fit-content'},
            }}
            image={adImages[ad.img_url]}
            alt={ad.description}
          />
        </Box>
        <Box component='div' sx={{minHeight: 57, height: 72, mt: 1, mb: 1}}>
          <Typography paragraph sx={{fontSize: 15, p: 0, m: 0, lineHeight: 1.2}}>
            <b>{ad.location}</b>
          </Typography>
          <Typography paragraph sx={{fontSize: 15, p: 0, m: 0, lineHeight: 1.2}}>
            {ad.description}
          </Typography>
          <Typography paragraph sx={{fontSize: 15, p: 0, m: 0, lineHeight: 1.2}}>
            {ad.status}
          </Typography>
        </Box>
      </Card>
    </IconButton>
  );

  // Hauptinhalt
  const isLoading = loadingAds || rows.length !== Object.keys(adImages).length;

  return (
    <Box
      sx={{
        p: {xs: 1, sm: 2, md: 3}, // Responsive padding: kleiner auf Mobile, größer auf Desktop
      }}>
      <CardHeader
        title={
          <Box sx={{textAlign: 'left'}}>
            <Typography
              variant='h4'
              sx={{
                fontWeight: 'bold',
              }}>
              Wir bauen für Sie:
            </Typography>
            <BlueAccent />
          </Box>
        }
      />
      <CardContent>
        {isLoading && renderLoadingSkeleton()}

        {!isLoading && filteredAds.length > 0 && (
          <Box
            component='div'
            sx={{
              width: '100%',
              height: {xs: 'inherit', md: '100%'},
              position: 'relative',
              overflow: 'hidden',
            }}
            onTouchStart={!matchesMd ? handleTouchStart : undefined}
            onTouchMove={!matchesMd ? handleTouchMove : undefined}
            onTouchEnd={!matchesMd ? handleTouchEnd : undefined}>
            {/* Wrapper that slides */}
            <Box
              component='div'
              sx={{
                display: 'flex',
                flexDirection: {xs: 'column', md: 'row'},
                width: isAnimating ? '200%' : '100%',
                height: {xs: 'inherit', md: '100%'},
                position: 'relative',
                left: isAnimating && direction === 'right' ? '-100%' : '0%',
                transition: isAnimating ? 'transform 0.5s ease-in-out' : 'none',
                transform: isAnimating
                  ? direction === 'left'
                    ? 'translateX(-50%)'
                    : 'translateX(50%)'
                  : 'translateX(0)',
              }}>
              {/* For sliding right, show incoming slide first */}
              {isAnimating && direction === 'right' && nextAds.length > 0 && (
                <Box
                  component='div'
                  sx={{
                    width: '50%',
                    height: {xs: 'inherit', md: '100%'},
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'},
                    justifyContent: 'space-between',
                    gap: {md: 2, xs: 2},
                    flexShrink: 0,
                  }}>
                  {nextAds.map((ad) => ad && renderAdCard(ad))}
                </Box>
              )}

              {/* Current slide */}
              <Box
                component='div'
                sx={{
                  width: isAnimating ? '50%' : '100%',
                  height: {xs: 'inherit', md: '100%'},
                  display: 'flex',
                  flexDirection: {xs: 'column', md: 'row'},
                  justifyContent: 'space-between',
                  gap: {md: 2, xs: 2},
                  flexShrink: 0,
                }}>
                {currentAds.map((ad) => ad && renderAdCard(ad))}
              </Box>

              {/* For sliding left, show incoming slide after */}
              {isAnimating && direction === 'left' && nextAds.length > 0 && (
                <Box
                  component='div'
                  sx={{
                    width: '50%',
                    height: {xs: 'inherit', md: '100%'},
                    display: 'flex',
                    flexDirection: {xs: 'column', md: 'row'},
                    justifyContent: 'space-between',
                    gap: {md: 2, xs: 2},
                    flexShrink: 0,
                  }}>
                  {nextAds.map((ad) => ad && renderAdCard(ad))}
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* Stepper für Desktop */}
        <Box component='div' sx={{display: 'flex', justifyContent: 'center'}}>
          {matchesMd && filteredAds.length === 0 && (
            <Skeleton variant='rounded' width={400} height={30} sx={{m: 3}} />
          )}

          {matchesMd && filteredAds.length > 0 && (
            <MobileStepper
              variant='dots'
              steps={filteredAds.length}
              position='static'
              activeStep={activeStep}
              sx={{maxWidth: 400, mt: 1}}
              nextButton={
                <IconButton
                  size='small'
                  onClick={() => {
                    if (autoPlayTimer) clearInterval(autoPlayTimer);
                    moveToNextSlide();
                    startAutoSlider();
                  }}
                  disabled={filteredAds.length === 0 || isAnimating}>
                  <ChevronRightRounded />
                </IconButton>
              }
              backButton={
                <IconButton
                  size='small'
                  onClick={() => {
                    if (autoPlayTimer) clearInterval(autoPlayTimer);
                    moveToPrevSlide();
                    startAutoSlider();
                  }}
                  disabled={filteredAds.length === 0 || isAnimating}>
                  <ChevronLeftRounded />
                </IconButton>
              }
            />
          )}

          {/* Stepper für Mobile */}
          {!matchesMd && filteredAds.length > 0 && (
            <MobileStepper
              variant='dots'
              steps={filteredAds.length}
              position='static'
                activeStep={activeStep}
              sx={{mt: 1}}
              nextButton={<div></div>}
              backButton={<div></div>}
            />
          )}
        </Box>
      </CardContent>
    </Box>
  );
}
